/**
 * Route prefetching utility.
 * Maps route path prefixes → dynamic import() functions.
 * When a user hovers over a nav link, we fire the import early
 * so the chunk is already loaded (or loading) by the time they click.
 *
 * Each import() is a no-op once the chunk is cached by webpack,
 * so duplicate calls are safe and free.
 */

const prefetchMap = {
  // Brand
  '/brand/dashboard':       () => import('./pages/BrandDashboard'),
  '/brand/campaigns':       () => import('./pages/BrandCampaignsNew'),
  '/brand/creators':        () => import('./pages/BrandFindCreators'),
  '/brand/agencies':        () => import('./pages/BrowseAgencies'),
  '/brand/freelancers':     () => import('./pages/BrowseFreelancers'),
  '/brand/jobs':            () => import('./pages/BrandJobManagement'),
  '/brand/messages':        () => import('./pages/BrandMessagesNew'),
  '/brand/templates':       () => import('./pages/BrandTemplatesNew'),
  '/brand/drafts':          () => import('./pages/BrandDraftsNew'),
  '/brand/analytics':       () => import('./pages/BrandAnalyticsDashboard'),
  '/brand/billing':         () => import('./pages/Billing'),
  '/brand/profile-settings':() => import('./pages/ProfileSettings'),
  '/brand/ai':              () => import('./pages/BrandAIScriptHub'),
  '/brand/post-job':        () => import('./pages/BrandPostJob'),

  // Creator
  '/creator/dashboard':     () => import('./pages/CreatorDashboardComplete'),
  '/creator/campaigns':     () => import('./pages/CreatorBrowseCampaigns'),
  '/creator/applications':  () => import('./pages/CreatorApplications'),
  '/creator/invitations':   () => import('./pages/CreatorInvitations'),
  '/creator/jobs':          () => import('./pages/CreatorJobManagement'),
  '/creator/messages':      () => import('./pages/CreatorMessages'),
  '/creator/earnings':      () => import('./pages/CreatorEarnings'),
  '/creator/analytics':     () => import('./pages/CreatorAnalytics'),
  '/creator/portfolio':     () => import('./pages/PortfolioManager'),
  '/creator/profile-settings':() => import('./pages/ProfileSettings'),
  '/creator/ai':            () => import('./pages/CreatorAIScriptHub'),

  // Freelancer
  '/freelancer/dashboard':  () => import('./pages/FreelancerDashboardMain'),
  '/freelancer/jobs':       () => import('./pages/FreelancerJobBrowse'),
  '/freelancer/applications':() => import('./pages/FreelancerApplications'),
  '/freelancer/messages':   () => import('./pages/FreelancerMessages'),
  '/freelancer/earnings':   () => import('./pages/FreelancerEarnings'),
  '/freelancer/deliverables':() => import('./pages/FreelancerDeliverables'),
  '/freelancer/portfolio':  () => import('./pages/FreelancerPortfolioShowcase'),
  '/freelancer/profile-settings':() => import('./pages/ProfileSettings'),
  '/freelancer/ai':         () => import('./pages/FreelancerAIScriptHub'),

  // Agency
  '/agency/dashboard':      () => import('./pages/AgencyDashboard'),
  '/agency/clients':        () => import('./pages/AgencyClientManagement'),
  '/agency/team':           () => import('./pages/AgencyTeamManagement'),
  '/agency/projects':       () => import('./pages/AgencyProjects'),
  '/agency/messages':       () => import('./pages/AgencyMessages'),
  '/agency/analytics':      () => import('./pages/AgencyAnalytics'),
  '/agency/revenue':        () => import('./pages/AgencyRevenue'),
  '/agency/settings':       () => import('./pages/AgencySettings'),
  '/agency/billing':        () => import('./pages/AgencyBilling'),
  '/agency/ai':             () => import('./pages/AgencyAIScriptHub'),

  // Admin
  '/admin':                 () => import('./pages/AdminDashboard'),
  '/admin/users':           () => import('./pages/AdminUserManagement'),
  '/admin/approvals':       () => import('./pages/AdminAccountApprovals'),
  '/admin/financials':      () => import('./pages/AdminFinancials'),
  '/admin/disputes':        () => import('./pages/AdminDisputes'),
  '/admin/content':         () => import('./pages/AdminContentModeration'),
  '/admin/audit':           () => import('./pages/AdminAuditLogs'),
  '/admin/settings':        () => import('./pages/AdminSettings'),
  '/admin/notifications':   () => import('./pages/AdminNotifications'),

  // Public pages
  '/':                      () => import('./pages/Home'),
  '/for-brands':            () => import('./pages/ForBrands'),
  '/for-creators':          () => import('./pages/ForCreators'),
  '/how-it-works':          () => import('./pages/HowItWorks'),
  '/case-studies':          () => import('./pages/CaseStudies'),
  '/about':                 () => import('./pages/About'),
  '/join':                  () => import('./pages/Join'),
  '/login':                 () => import('./pages/Login'),
};

// Track already-prefetched paths to avoid redundant calls
const prefetched = new Set();

/**
 * Prefetch the JS chunk for a given route path.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function prefetchRoute(path) {
  if (!path || prefetched.has(path)) return;

  // Find exact match first, then prefix match
  const loader = prefetchMap[path] ||
    prefetchMap[Object.keys(prefetchMap).find(k => path.startsWith(k + '/'))];

  if (loader) {
    prefetched.add(path);
    // Fire and forget — we don't need the result,
    // just want webpack to start downloading the chunk
    loader().catch(() => {
      // If the prefetch fails (e.g., offline), remove from set so it can retry
      prefetched.delete(path);
    });
  }
}

/**
 * Returns an onMouseEnter handler that prefetches the route chunk.
 * Usage: <button onMouseEnter={prefetchOnHover('/brand/campaigns')} ...>
 */
export function prefetchOnHover(path) {
  return () => prefetchRoute(path);
}

export default prefetchRoute;
